import { AlertCircle, LoaderCircle, LogIn, RefreshCcw, ShieldCheck } from 'lucide-react';
import { Button } from './ui';
import type { EmbeddedScreenState } from '../lib/embeddedShell';

interface EmbeddedEntryPageProps {
  state: EmbeddedScreenState;
  onRequestLauncherLogin: () => void;
  onRetry: () => void;
  presentation?: 'page' | 'overlay';
}

const copyByState: Record<
  EmbeddedScreenState,
  {
    eyebrow: string;
    badge: string;
    title: string;
    description: string;
    helper?: string;
  }
> = {
  booting: {
    eyebrow: 'Conexao protegida com o Launcher',
    badge: 'Inicializando',
    title: 'Conectando ao Launcher...',
    description: 'Estamos validando a disponibilidade da sessao antes de liberar este painel.',
    helper: 'Esse passo evita que o addon opere sem uma sessao local valida.'
  },
  opening_launcher: {
    eyebrow: 'Autenticacao gerenciada pelo Launcher',
    badge: 'Abrindo Launcher',
    title: 'Abrindo o Launcher...',
    description: 'Estamos abrindo ou trazendo o Launcher para frente para concluir sua autenticacao.',
    helper: 'Isso pode levar alguns segundos. Assim que a sessao for publicada, este painel sera liberado automaticamente.'
  },
  waiting_launcher_login: {
    eyebrow: 'Autenticacao gerenciada pelo Launcher',
    badge: 'Painel bloqueado',
    title: 'Aguardando login no Launcher',
    description: 'Abra o Launcher e faca login para liberar este painel com seguranca.',
    helper: 'Enquanto isso, o painel permanece bloqueado para proteger sua sessao.'
  },
  reconnecting: {
    eyebrow: 'Autenticacao gerenciada pelo Launcher',
    badge: 'Reconectando',
    title: 'Reconectando...',
    description: 'Mantivemos esta tela aberta enquanto aguardamos uma nova sessao do Launcher.',
    helper: 'Voce nao precisa recarregar o Corel. Assim que a sessao voltar, o painel continua daqui.'
  },
  authenticated: {
    eyebrow: 'Autenticacao gerenciada pelo Launcher',
    badge: 'Sessao validada',
    title: 'Conectado via Launcher',
    description: 'Sua sessao foi validada. Abrindo o painel...',
    helper: 'Preparando o contexto seguro do painel.'
  }
};

export function EmbeddedEntryPage({
  state,
  onRequestLauncherLogin,
  onRetry,
  presentation = 'page'
}: EmbeddedEntryPageProps) {
  const copy = copyByState[state];
  const isBusy =
    state === 'booting' ||
    state === 'opening_launcher' ||
    state === 'reconnecting' ||
    state === 'authenticated';
  const isOverlay = presentation === 'overlay';
  const showActions = state === 'waiting_launcher_login';

  return (
    <div
      className={
        isOverlay
          ? 'fixed inset-0 z-[80] bg-navy-950/72 backdrop-blur-sm text-white flex items-center justify-center px-6'
          : 'h-full min-h-screen w-full bg-navy-900 text-white flex items-center justify-center px-6'
      }
    >
      <div
        className={
          isOverlay
            ? 'w-full max-w-lg rounded-3xl border border-navy-700/70 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.14),_rgba(15,23,42,0.95)_55%)] shadow-2xl p-8'
            : 'w-full max-w-xl rounded-3xl border border-navy-700 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.12),_rgba(15,23,42,0.92)_55%)] shadow-2xl p-8'
        }
      >
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-500/15 text-blue-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
            {isBusy ? <LoaderCircle className="h-7 w-7 animate-spin" /> : <AlertCircle className="h-7 w-7" />}
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs uppercase tracking-[0.22em] text-blue-200/75">
                {copy.eyebrow}
              </p>
              <span className="inline-flex items-center gap-1 rounded-full border border-blue-400/20 bg-blue-400/10 px-2.5 py-1 text-[11px] font-medium text-blue-100">
                <ShieldCheck className="h-3.5 w-3.5" />
                {copy.badge}
              </span>
            </div>
            <h1 className="mt-2 text-2xl font-semibold">{copy.title}</h1>
            <p className="mt-3 text-sm text-slate-300">{copy.description}</p>
            {copy.helper && <p className="mt-3 text-sm text-slate-400">{copy.helper}</p>}
          </div>
        </div>

        {(state === 'opening_launcher' || state === 'reconnecting') && (
          <div className="mt-8 h-2 overflow-hidden rounded-full bg-navy-950/70">
            <div className="h-full w-1/3 animate-pulse rounded-full bg-blue-400/80" />
          </div>
        )}

        {showActions && (
          <>
            <div className="mt-8 rounded-2xl border border-white/8 bg-navy-950/35 px-4 py-3 text-sm text-slate-300">
              <p className="font-medium text-slate-100">Como liberar este painel</p>
              <ol className="mt-2 space-y-1 text-slate-400">
                <li>1. Abra o Launcher OneTech.</li>
                <li>2. Faça login na conta correta.</li>
                <li>3. Volte para este painel ou use “Tentar novamente”.</li>
              </ol>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
            <Button
              variant="primary"
              size="md"
              icon={LogIn}
              onClick={onRequestLauncherLogin}
              className="shadow-lg shadow-blue-950/40"
            >
              Abrir Launcher
            </Button>
            <Button
              variant="ghost"
              size="md"
              icon={RefreshCcw}
              onClick={onRetry}
            >
              Tentar novamente
            </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
